import {useQuery} from '@tanstack/react-query';
import {WorkspaceQueryKeys} from './requests/workspace-query-keys';
import {Workspace} from './types/workspace';
import {BackendResponse} from '../http/backend-response/backend-response';
import {apiClient} from '../http/query-client';
import {useAuth} from '../auth/use-auth';

export interface FetchUserWorkspacesResponse extends BackendResponse {
  workspaces: Workspace[];
}

export const PersonalWorkspace: Workspace = {
  name: 'My Drive',
  default: true,
  id: 0,
  members_count: 1,
};

function fetchUserWorkspaces(): Promise<FetchUserWorkspacesResponse> {
  return apiClient.get(`me/workspaces`).then(response => response.data);
}

// "My Drive" is the user's personal workspace row (always end-to-end
// encrypted) once it exists; before that — or for a user who hasn't set up
// encryption yet — the legacy implicit drive (id 0) stands in for it.
function addPersonalWorkspaceToResponse(response: FetchUserWorkspacesResponse) {
  const personal = response.workspaces.find(w => w.personal);
  if (personal) {
    return [
      {...personal, name: PersonalWorkspace.name, default: true},
      ...response.workspaces.filter(w => w !== personal),
    ];
  }
  return [PersonalWorkspace, ...response.workspaces];
}

// id of the user's "My Drive" (personal workspace row, or 0 before it exists)
export function personalWorkspaceIdOf(workspaces?: Workspace[]): number {
  return workspaces?.find(w => w.personal)?.id ?? PersonalWorkspace.id;
}

export function useUserWorkspaces() {
  // Anonymous visitors (e.g. on a public shareable-link page) can be
  // wrapped in `ActiveWorkspaceProvider` too, but `/me/workspaces`
  // requires auth and 401s for them. Skip the request when there's no
  // session — the placeholderData (empty list + injected personal
  // workspace) is still surfaced via `select`.
  const {user} = useAuth();
  return useQuery({
    queryKey: WorkspaceQueryKeys.fetchUserWorkspaces,
    queryFn: fetchUserWorkspaces,
    placeholderData: {workspaces: []},
    select: addPersonalWorkspaceToResponse,
    enabled: !!user,
  });
}
